import React from "react";
import { Label } from "@/ui/label";
import { cn } from "@/lib/utils";
import { getAuthorizedTenants } from "../../../../Utils/Auth/ValidatePermissions";
import { Search, ChevronDown, Check } from "lucide-react";
import { Input } from "@/ui/input";

type Tenant = {
  TenantId: string | number;
  TenantName: string;
  [k: string]: any;
};

type TenantDropdownProps = {
  textField: string; // e.g. "TenantName"
  label?: string;
  className?: string;
  dataItemKey: string; // e.g. "TenantId"
  tenant?: Tenant | null;
  defaultItem?: Partial<Tenant> & { disabled?: boolean };
  tenantFormId: string; // e.g. "TenantID"
  tenantFormName: string; // e.g. "TenantName"
  setFormData: (updater: (prev: any) => any) => void;
  setTenant: (t: Tenant | undefined) => void;
  editMode?: string; // "ReadOnly" or ""
  filterable?: boolean;
  requiredField?: boolean;
};

const ReadOnlyValue: React.FC<{
  value: string;
  prefix?: string;
  required?: boolean;
}> = ({ value, prefix, required }) => (
  <div className="h-10 flex items-center text-sm">
    {prefix && (
      <span className="text-xs font-semibold text-muted-foreground mr-1">
        {prefix}
        {required && <span className="text-destructive ml-0.5"></span>}:
      </span>
    )}
    <span className="text-foreground">{value || "—"}</span>
  </div>
);

const TenantDropdown: React.FC<TenantDropdownProps> = ({
  textField,
  label,
  className,
  dataItemKey,
  tenant,
  defaultItem,
  tenantFormId,
  tenantFormName,
  setFormData,
  setTenant,
  editMode,
  filterable,
  requiredField,
}) => {
  const tenantList = React.useMemo<Tenant[]>(
    () => (getAuthorizedTenants() as Tenant[]) || [],
    [],
  );

  const disabled = (tenantList?.length || 0) === 1 || editMode === "ReadOnly";

  const selectedName =
    (tenant && (tenant as any)[textField]) ||
    (defaultItem && (defaultItem as any)[textField]) ||
    "";

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filtered = React.useMemo(() => {
    if (!filterable || !query.trim()) return tenantList;
    const q = query.trim().toLowerCase();
    return tenantList.filter((t) =>
      String((t as any)[textField] ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [filterable, query, tenantList, textField]);

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(t)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSelect = (selectedId: string | number) => {
    const t = tenantList.find(
      (x) => String((x as any)[dataItemKey]) === String(selectedId),
    ) as Tenant | undefined;

    setTenant(t);
    setFormData((prev: any) => ({
      ...prev,
      [tenantFormId]: t?.TenantId ?? "",
      [tenantFormName]: t?.TenantName ?? "",
    }));

    setQuery("");
    setOpen(false);
  };

  // Read-only presentation
  if (editMode === "ReadOnly") {
    return (
      <div className={cn("space-y-1", className)}>
        <ReadOnlyValue
          prefix={label}
          required={requiredField}
          value={selectedName}
        />
      </div>
    );
  }

  // Filterable combobox-like UI
  if (filterable) {
    return (
      <div className={cn("space-y-1", className)} ref={wrapperRef}>
        {!disabled && label && (
          <Label className="text-xs text-muted-foreground">
            {label}
            {requiredField && (
              <span className="text-destructive ml-0.5">*</span>
            )}
          </Label>
        )}
        {disabled ? (
          <ReadOnlyValue
            prefix={label}
            required={requiredField}
            value={selectedName}
          />
        ) : (
          <div className="relative">
            <input
              type="text"
              value={open ? query : selectedName}
              onChange={(e) => {
                setQuery(e.currentTarget.value);
              }}
              onFocus={() => {
                setOpen(true);
                setQuery("");
              }}
              placeholder={defaultItem?.[textField] as string | undefined}
              disabled={disabled}
              className={cn(
                "h-10 w-full rounded-lg border border-border bg-background pl-8 pr-8 text-sm",
                "text-foreground placeholder:text-muted-foreground caret-foreground",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
                disabled && "opacity-60 cursor-not-allowed",
              )}
              aria-haspopup="listbox"
              aria-expanded={open ? "true" : "false"}
            />
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <ChevronDown
              className={cn(
                "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
            {open && (
              <div
                role="listbox"
                className={cn(
                  "absolute left-0 right-0 z-50 mt-1 rounded-lg border border-border bg-popover text-popover-foreground",
                  "shadow-lg ring-1 ring-border/50 overflow-hidden",
                  // Ensure dropdown is always above legacy content sliders / kendo popups
                  "z-[250]",
                )}
              >
                <ul className="max-h-56 overflow-auto py-1 list-none m-0 p-0">
                  {filtered.map((t) => {
                    const id = (t as any)[dataItemKey] as string | number;
                    const name = (t as any)[textField] as string;
                    const active =
                      tenant && (tenant as any)[dataItemKey] === id;
                    return (
                      <li key={String(id)}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active ? "true" : "false"}
                          className={cn(
                            "flex w-full items-center gap-2 px-3 py-2 text-sm outline-none",
                            "hover:bg-accent/60 hover:text-accent-foreground",
                            active && "bg-accent/70 text-accent-foreground",
                          )}
                          onClick={() => handleSelect(id)}
                        >
                          <span className="inline-flex w-5 justify-center">
                            {active ? (
                              <Check className="h-4 w-4 text-foreground" />
                            ) : null}
                          </span>
                          <span className="truncate">{name}</span>
                        </button>
                      </li>
                    );
                  })}
                  {filtered.length === 0 && (
                    <li className="px-3 py-2 text-sm text-muted-foreground">
                      No results
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Non-filterable: native select
  return (
    <div className={cn("space-y-1", className)}>
      {!disabled && label && (
        <Label className="text-xs text-muted-foreground">
          {label}
          {requiredField && <span className="text-destructive ml-0.5">*</span>}
        </Label>
      )}
      {disabled ? (
        <ReadOnlyValue
          prefix={label}
          required={requiredField}
          value={selectedName}
        />
      ) : (
        <div className="relative">
          <select
            className={cn(
              "appearance-none h-10 w-full rounded-lg border border-border bg-background pr-9 pl-3 text-sm",
              "text-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
              disabled && "opacity-60 cursor-not-allowed",
            )}
            value={
              tenant ? ((tenant as any)[dataItemKey] as string | number) : ""
            }
            onChange={(e) => {
              const selectedId = (e.target as HTMLSelectElement).value;
              handleSelect(selectedId);
            }}
            disabled={disabled}
          >
            {/* Default option */}
            <option value="">
              {(defaultItem && (defaultItem as any)[textField]) || "Select..."}
            </option>
            {tenantList.map((t) => {
              const id = (t as any)[dataItemKey] as string | number;
              const name = (t as any)[textField] as string;
              return (
                <option key={String(id)} value={String(id)}>
                  {name}
                </option>
              );
            })}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default TenantDropdown;
